import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";

const UPDATED = "5 February 2026";

export default function Refunds() {
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-accent transition-colors"
        data-testid="refunds-back"
      >
        <ArrowLeft size={14} /> Back to home
      </Link>
      <p className="mt-8 text-primary font-semibold uppercase tracking-wider text-xs">Legal</p>
      <h1 className="font-display font-bold text-4xl md:text-5xl text-accent mt-2 tracking-tight">
        Refund policy
      </h1>
      <p className="text-sm text-muted-foreground mt-3">Last updated: {UPDATED}</p>

      <div className="mt-10 space-y-8 text-accent/85 leading-relaxed">
        <div className="rounded-3xl bg-primary/10 border border-primary/30 p-6">
          <h2 className="font-display font-bold text-lg text-accent">The short version</h2>
          <p className="text-[15px] mt-2">
            Fetchright Unlock is a one-time <strong>$2 AUD</strong> payment for lifetime access —
            no subscription, nothing auto-renews. If you're not happy with your unlock, we offer a
            <strong> 7-day full refund</strong>, no questions asked.
          </p>
        </div>

        <Section title="1. Nothing to cancel">
          <p>
            Fetchright Unlock is a single one-time payment. There is no monthly subscription,
            no auto-renewal, and nothing to cancel. Once you've paid, you keep access forever.
          </p>
        </Section>

        <Section title="2. 7-day full refund">
          <p>
            If you change your mind within <strong>7 days</strong> of paying, we'll refund the full
            $2 — no reason required. Email us at{" "}
            <a className="text-primary underline" href="mailto:support@fetchright.net">
              support@fetchright.net
            </a>{" "}
            from the email you used at checkout (it's on your Stripe receipt). Refunds are processed within
            5-10 business days back to your original payment method. Your unlocked access will be
            removed on refund.
          </p>
        </Section>

        <Section title="3. Accidental or duplicate charges">
          <p>
            If you were charged twice or believe there was an error, contact us within 60 days and
            we'll investigate. Genuine errors are refunded in full regardless of the 7-day window.
          </p>
        </Section>

        <Section title="4. When refunds are not available">
          <p>
            Outside the 7-day window and for reasons other than a genuine error, the one-time
            $2 payment is non-refundable.
          </p>
        </Section>

        <Section title="5. Australian Consumer Law">
          <p>
            Our policy is provided in addition to your rights under the Australian Consumer Law (ACL).
            If Fetchright Unlock fails to deliver as described, you may be entitled to a refund,
            repair, or replacement under the ACL. Nothing in this policy limits those rights.
          </p>
        </Section>

        <Section title="6. How to request a refund">
          <div className="rounded-2xl border border-border/70 bg-muted/40 p-5 space-y-3 text-[15px]">
            <p className="flex items-center gap-2 font-semibold text-accent">
              <Mail size={16} className="text-primary" /> Email support@fetchright.net
            </p>
            <p>Include the following so we can process it quickly:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>The email address you used at checkout (it's on your Stripe receipt).</li>
              <li>The date of the charge (or Stripe receipt number if you have it).</li>
              <li>A short note on the reason (optional but helps us improve).</li>
            </ul>
            <p className="text-xs text-muted-foreground">
              We aim to reply within 2 business days.
            </p>
          </div>
        </Section>

        <Section title="7. Disputes and chargebacks">
          <p>
            If you have concerns about a payment, please contact us first — a direct refund is usually
            faster than a bank dispute. Chargebacks filed without contacting us may result in your
            unlock being revoked.
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-bold text-xl text-accent mb-3">{title}</h2>
      <div className="text-[15px]">{children}</div>
    </section>
  );
}
